import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { uploadToCloudinary } from "../../utils/cloudinary";

const SellerRegisterModal = ({ closeModal }) => {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [errorMsg, setErrorMsg] = useState("");
  const [form, setForm] = useState({
    fullName: "",
    mobNo: "",
    emailId: "",
    gender: "",
    address: "",
    businessName: "",
    businessType: "",
    businessRegistrationName: "",
    panNo: "",
    gstIn: "",
    commission: 10,
    bulkOrderCommission: 5,
    shopImage: "",
    panImage: "",
    addressProof: "",
    ownerIdProof: "",
    gstCertificate: "",
    otherDocuments: [],
  });

  const [preview, setPreview] = useState({
    shopImage: "",
    panImage: "",
    addressProof: "",
    ownerIdProof: "",
    gstCertificate: "",
    otherDocuments: [],
  });

  useEffect(() => {
    return () => {
      Object.values(preview).forEach(url => {
        if (url && typeof url === 'string' && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      preview.otherDocuments.forEach(url => {
        if (url && typeof url === 'string' && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleFile = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && !file.type.includes('pdf')) {
      toast.error("Please upload only images or PDF files");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }

    setForm({ ...form, [field]: file });
    
    if (file.type.startsWith('image/')) {
      setPreview(prev => ({ 
        ...prev, 
        [field]: URL.createObjectURL(file) 
      }));
    }
  };

  const handleMultipleFiles = (e) => {
    const files = Array.from(e.target.files);
    
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/') && !file.type.includes('pdf')) {
        toast.error(`Skipping ${file.name}: Only images or PDF files allowed`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`Skipping ${file.name}: File size should be less than 5MB`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setForm(prev => ({ 
      ...prev, 
      otherDocuments: [...prev.otherDocuments, ...validFiles] 
    }));

    const imagePreviews = validFiles
      .filter(file => file.type.startsWith('image/'))
      .map(file => URL.createObjectURL(file));

    setPreview(prev => ({
      ...prev,
      otherDocuments: [...prev.otherDocuments, ...imagePreviews]
    }));
  };

  const removeOtherDocument = (index) => {
    setForm(prev => ({
      ...prev,
      otherDocuments: prev.otherDocuments.filter((_, i) => i !== index)
    }));

    const previewUrl = preview.otherDocuments[index];
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreview(prev => ({
      ...prev,
      otherDocuments: prev.otherDocuments.filter((_, i) => i !== index)
    }));
  };

  const upload = async (file) => {
    if (!file) return "";
    if (file instanceof File) {
      const result = await uploadToCloudinary(file);
      return result.secure_url || result.url;
    }
    return file;
  };

  const handleSubmit = async () => {
    setErrorMsg("");
    if (!form.fullName || !form.mobNo || !form.emailId || !form.businessName) {
      setErrorMsg("Please fill in all required fields (Name, Mobile, Email, Business Name)");
      setCurrentStep(1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      setLoading(true);

      const payload = { ...form };

      const uploadPromises = [
        upload(form.shopImage),
        upload(form.panImage),
        upload(form.addressProof),
        upload(form.ownerIdProof),
        upload(form.gstCertificate),
      ];

      const [
        shopImageUrl,
        panImageUrl,
        addressProofUrl,
        ownerIdProofUrl,
        gstCertificateUrl,
      ] = await Promise.all(uploadPromises);

      payload.shopImage = shopImageUrl;
      payload.panImage = panImageUrl;
      payload.addressProof = addressProofUrl;
      payload.ownerIdProof = ownerIdProofUrl;
      payload.gstCertificate = gstCertificateUrl;

      const otherDocsUploads = form.otherDocuments.map(doc => upload(doc));
      payload.otherDocuments = await Promise.all(otherDocsUploads);

      payload.permissions = {
        "supplier-dashboard": true,
        "supplier-categories": true,
        "supplier-subCategories": true,
        "supplier-tier3Categories": true,
        "supplier-products": true,
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/supplier/suppliers/register`,
        payload
      );

      toast.success("Seller registered successfully!");
      closeModal(); // Go back to login

    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || err?.response?.data?.message || "Registration failed";
      setErrorMsg(msg);
      
      const lowerErr = msg.toLowerCase();
      if (lowerErr.includes("pan") || lowerErr.includes("gst") || lowerErr.includes("business") || lowerErr.includes("commission")) {
        setCurrentStep(2);
      } else if (lowerErr.includes("name") || lowerErr.includes("mob") || lowerErr.includes("phone") || lowerErr.includes("email") || lowerErr.includes("gender") || lowerErr.includes("address")) {
        setCurrentStep(1);
      } else if (lowerErr.includes("image") || lowerErr.includes("proof") || lowerErr.includes("certificate") || lowerErr.includes("document")) {
        setCurrentStep(3);
      }
      
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    setErrorMsg("");
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };
  const prevStep = () => {
    setErrorMsg("");
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center pt-8 p-4 font-sans text-[#111]">
      {/* Amazon-like Logo */}
      <div className="mb-6 cursor-pointer" onClick={closeModal}>
        <img 
          src={require("../../Assets/Logos/Bharat Karigari N.png")} 
          alt="Bharat Karigari Logo" 
          className="h-12 object-contain"
        />
      </div>

      <div className="bg-white w-full max-w-[600px] border border-[#ddd] rounded-[4px] p-6 shadow-[0_1px_2px_rgba(0,0,0,0.2)] mb-10">
        
        {errorMsg && (
          <div className="mb-6 border border-[#c40000] rounded-[3px] p-4 flex gap-3 shadow-[0_0_0_4px_rgba(221,0,0,0.1)] bg-white">
            <div className="text-[#c40000] pt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <div>
              <h4 className="text-[#c40000] font-bold text-[13px] mb-0.5">There was a problem</h4>
              <p className="text-[13px] m-0 text-[#111]">{errorMsg}</p>
            </div>
          </div>
        )}

        <h1 className="text-[28px] font-normal mb-6">Create Seller Account</h1>
        
        {/* Step Progress */}
        <div className="flex justify-between items-center mb-8 border-b border-[#e7e7e7] pb-4">
          {[
            { step: 1, label: "Personal Info" },
            { step: 2, label: "Business Info" },
            { step: 3, label: "Documents" }
          ].map(s => (
            <div key={s.step} className={`flex items-center ${currentStep === s.step ? 'text-[#e77600] font-bold' : (currentStep > s.step ? 'text-green-600' : 'text-[#767676]')}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[12px] text-white mr-2 ${currentStep === s.step ? 'bg-[#e77600]' : (currentStep > s.step ? 'bg-green-600' : 'bg-[#767676]')}`}>
                {currentStep > s.step ? '✓' : s.step}
              </div>
              <span className="text-[13px] hidden sm:inline">{s.label}</span>
            </div>
          ))}
        </div>

        {/* STEP 1 */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <h3 className="text-[18px] font-bold mb-4">Personal Information</h3>
            <Input label="Your name *" name="fullName" value={form.fullName} onChange={handleChange} placeholder="First and last name" errorMsg={errorMsg} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Mobile number *" name="mobNo" value={form.mobNo} onChange={handleChange} type="tel" errorMsg={errorMsg} />
              <Input label="Email *" name="emailId" value={form.emailId} onChange={handleChange} type="email" errorMsg={errorMsg} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Gender" name="gender" value={form.gender} onChange={handleChange} errorMsg={errorMsg} />
              <Input label="Address" name="address" value={form.address} onChange={handleChange} errorMsg={errorMsg} />
            </div>
            
            <button onClick={nextStep} className="w-full mt-6 py-1.5 bg-[#f0c14b] border border-[#a88734_#9c7e31_#846a29] hover:bg-[#f4d078] rounded-[3px] text-[13px] text-[#111] shadow-[0_1px_0_rgba(255,255,255,0.4)_inset]">
              Next Step
            </button>
          </div>
        )}

        {/* STEP 2 */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <h3 className="text-[18px] font-bold mb-4">Business Information</h3>
            <Input label="Business Name *" name="businessName" value={form.businessName} onChange={handleChange} errorMsg={errorMsg} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Business Type" name="businessType" value={form.businessType} onChange={handleChange} errorMsg={errorMsg} />
              <Input label="Registration Name" name="businessRegistrationName" value={form.businessRegistrationName} onChange={handleChange} errorMsg={errorMsg} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="PAN No" name="panNo" value={form.panNo} onChange={handleChange} errorMsg={errorMsg} />
              <Input label="GST IN" name="gstIn" value={form.gstIn} onChange={handleChange} errorMsg={errorMsg} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Commission (%)" type="number" name="commission" value={form.commission} onChange={handleChange} min="0" max="100" errorMsg={errorMsg} />
              <Input label="Bulk Order Commission (%)" type="number" name="bulkOrderCommission" value={form.bulkOrderCommission} onChange={handleChange} min="0" max="100" errorMsg={errorMsg} />
            </div>
            
            <div className="flex gap-4 mt-6">
              <button onClick={prevStep} className="w-1/3 py-1.5 bg-[#e7e9ec] border border-[#adb1b8_#a2a6ac_#8d9096] hover:bg-[#d9dce1] rounded-[3px] text-[13px] text-[#111]">
                Back
              </button>
              <button onClick={nextStep} className="w-2/3 py-1.5 bg-[#f0c14b] border border-[#a88734_#9c7e31_#846a29] hover:bg-[#f4d078] rounded-[3px] text-[13px] text-[#111] shadow-[0_1px_0_rgba(255,255,255,0.4)_inset]">
                Next Step
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <h3 className="text-[18px] font-bold mb-4">Documents & Certificates</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <FileUpload title="Shop Image" onChange={(e) => handleFile(e, "shopImage")} preview={preview.shopImage} />
              <FileUpload title="PAN Image" onChange={(e) => handleFile(e, "panImage")} preview={preview.panImage} />
              <FileUpload title="GST Certificate" onChange={(e) => handleFile(e, "gstCertificate")} preview={preview.gstCertificate} />
              <FileUpload title="Owner ID Proof" onChange={(e) => handleFile(e, "ownerIdProof")} preview={preview.ownerIdProof} />
              <FileUpload title="Address Proof" onChange={(e) => handleFile(e, "addressProof")} preview={preview.addressProof} />
            </div>
            
            <div className="mt-4 border-t border-[#e7e7e7] pt-4">
              <MultipleFileUpload title="Other Documents" onChange={handleMultipleFiles} previews={preview.otherDocuments} onRemove={removeOtherDocument} fileCount={form.otherDocuments.length} />
            </div>
            
            <div className="mt-6 text-[12px] leading-relaxed mb-4">
              By creating an account, you agree to Bharat Karigari's <span className="text-[#0066c0] hover:text-[#c45500] hover:underline cursor-pointer">Conditions of Use</span> and <span className="text-[#0066c0] hover:text-[#c45500] hover:underline cursor-pointer">Privacy Notice</span>.
            </div>

            <div className="flex gap-4">
              <button onClick={prevStep} disabled={loading} className="w-1/3 py-1.5 bg-[#e7e9ec] border border-[#adb1b8_#a2a6ac_#8d9096] hover:bg-[#d9dce1] rounded-[3px] text-[13px] text-[#111] disabled:opacity-50">
                Back
              </button>
              <button onClick={handleSubmit} disabled={loading} className="w-2/3 py-1.5 bg-[#f0c14b] border border-[#a88734_#9c7e31_#846a29] hover:bg-[#f4d078] rounded-[3px] text-[13px] text-[#111] shadow-[0_1px_0_rgba(255,255,255,0.4)_inset] flex items-center justify-center font-bold">
                {loading ? "Registering..." : "Create your Seller account"}
              </button>
            </div>
          </div>
        )}

      </div>
      
      <div className="w-full max-w-[600px] text-center text-[11px] text-[#555] pb-8">
        <div className="flex justify-center gap-6 mb-2">
          <span className="text-[#0066c0] hover:text-[#c45500] hover:underline cursor-pointer">Conditions of Use</span>
          <span className="text-[#0066c0] hover:text-[#c45500] hover:underline cursor-pointer">Privacy Notice</span>
          <span className="text-[#0066c0] hover:text-[#c45500] hover:underline cursor-pointer">Help</span>
        </div>
        <div>© 2024-2026, BharatKarigari.com, Inc. or its affiliates</div>
      </div>
    </div>
  );
};

const Input = ({ label, name, type = "text", errorMsg = "", ...rest }) => {
  const hasError = errorMsg.toLowerCase().includes(name.toLowerCase());
  return (
    <div className="flex flex-col">
      <label className="text-[13px] font-bold mb-1">{label}</label>
      <input 
        type={type}
        name={name}
        {...rest} 
        className={`w-full px-3 py-2 border ${hasError ? 'border-[#c40000] shadow-[0_0_0_3px_rgba(221,0,0,0.1)] focus:border-[#c40000]' : 'border-[#a6a6a6] focus:border-[#e77600] focus:shadow-[0_0_3px_2px_rgba(228,121,17,0.5)]'} rounded-[3px] focus:outline-none text-[14px]`}
      />
    </div>
  );
};

const FileUpload = ({ title, onChange, preview, acceptedTypes = "image/*,application/pdf" }) => (
  <div className="flex flex-col">
    <label className="text-[13px] font-bold mb-1">{title}</label>
    <input 
      type="file" 
      onChange={onChange} 
      accept={acceptedTypes}
      className="text-[13px] border border-[#a6a6a6] rounded-[3px] focus:outline-none file:mr-3 file:py-1.5 file:px-3 file:border-0 file:bg-[#e7e9ec] file:border-[#adb1b8_#a2a6ac_#8d9096] file:border file:rounded-[3px] file:text-[13px] file:text-[#111] hover:file:bg-[#d9dce1]"
    />
    {preview && (
      <div className="mt-2">
        {preview.includes('blob:') ? (
          <img src={preview} className="w-full h-24 rounded object-cover border border-[#ddd]" alt="preview" />
        ) : (
          <div className="w-full h-24 rounded border border-[#ddd] flex items-center justify-center bg-[#f9f9f9]">
            <span className="text-[#767676] text-[12px]">PDF Document</span>
          </div>
        )}
      </div>
    )}
  </div>
);

const MultipleFileUpload = ({ title, onChange, previews, onRemove, fileCount, acceptedTypes = "image/*,application/pdf" }) => (
  <div className="flex flex-col">
    <label className="text-[13px] font-bold mb-1">
      {title} {fileCount > 0 && <span className="font-normal text-[#767676]">({fileCount} files)</span>}
    </label>
    <input 
      type="file" 
      multiple 
      onChange={onChange}
      accept={acceptedTypes}
      className="text-[13px] border border-[#a6a6a6] rounded-[3px] focus:outline-none file:mr-3 file:py-1.5 file:px-3 file:border-0 file:bg-[#e7e9ec] file:border-[#adb1b8_#a2a6ac_#8d9096] file:border file:rounded-[3px] file:text-[13px] file:text-[#111] hover:file:bg-[#d9dce1]"
    />
    
    {previews.length > 0 && (
      <div className="mt-3">
        <label className="text-[12px] text-[#767676] mb-2 block">Previews:</label>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {previews.map((preview, index) => (
            <div key={index} className="relative group">
              {preview.includes('blob:') ? (
                <img src={preview} className="w-full h-16 rounded object-cover border border-[#ddd]" alt={`doc ${index + 1}`} />
              ) : (
                <div className="w-full h-16 rounded border border-[#ddd] flex items-center justify-center bg-[#f9f9f9]">
                  <span className="text-[#767676] text-[10px]">PDF</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default SellerRegisterModal;